import React, { useState } from 'react';
import { FiInfo, FiX } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * AIInfoTooltip - A tooltip component for displaying information about AI features
 * 
 * @param {Object} props - Component props
 * @param {string} props.title - Title of the tooltip
 * @param {string|React.ReactNode} props.content - Content of the tooltip
 * @param {string} props.position - Position of the tooltip (top, bottom, left, right)
 * @param {string} props.className - Additional classes for the tooltip
 * @param {React.ReactNode} props.children - Children to render the tooltip around
 * @param {boolean} props.showIcon - Whether to show the info icon
 * @param {boolean} props.expandable - Whether the tooltip can be expanded to a modal
 */
const AIInfoTooltip = ({ 
  title = 'AI Information', 
  content, 
  position = 'top', 
  className = '',
  children,
  showIcon = true,
  expandable = false
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Calculate position styles
  const getPositionStyles = () => {
    switch (position) {
      case 'top':
        return 'bottom-full mb-2';
      case 'bottom':
        return 'top-full mt-2';
      case 'left':
        return 'right-full mr-2';
      case 'right':
        return 'left-full ml-2';
      default:
        return 'bottom-full mb-2';
    }
  };
  
  // Handle click on the tooltip trigger
  const handleClick = (e) => {
    e.stopPropagation();
    if (expandable) {
      setIsExpanded(true);
      setIsVisible(false);
    }
  };
  
  return (
    <div className="relative inline-flex" onMouseLeave={() => setIsVisible(false)}>
      <div 
        className="inline-flex items-center cursor-help"
        onMouseEnter={() => setIsVisible(true)}
        onClick={handleClick}
      >
        {children}
        {showIcon && (
          <div className="ml-1 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400">
            <FiInfo size={14} />
          </div>
        )}
      </div>
      
      {/* Tooltip */}
      <AnimatePresence>
        {isVisible && !isExpanded && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.1 }}
            className={`absolute z-50 ${getPositionStyles()} w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-3 text-left ${className}`}
          >
            <div className="flex justify-between items-center mb-1">
              <h4 className="font-medium text-gray-800 dark:text-white text-sm">{title}</h4>
              {expandable && (
                <button 
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsExpanded(true);
                    setIsVisible(false);
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M3 4a1 1 0 011-1h4a1 1 0 010 2H6.414l2.293 2.293a1 1 0 01-1.414 1.414L5 6.414V8a1 1 0 01-2 0V4zm9 1a1 1 0 010-2h4a1 1 0 011 1v4a1 1 0 01-2 0V6.414l-2.293 2.293a1 1 0 11-1.414-1.414L13.586 5H12z" clipRule="evenodd" />
                  </svg>
                </button>
              )}
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-300">
              {content}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Expanded Modal */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={() => setIsExpanded(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full max-h-[80vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-5 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                <h3 className="text-lg font-bold text-gray-800 dark:text-white">{title}</h3>
                <button
                  onClick={() => setIsExpanded(false)}
                  className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <FiX size={20} className="text-gray-500 dark:text-gray-400" />
                </button>
              </div>
              <div className="p-5 overflow-y-auto">
                <div className="text-gray-600 dark:text-gray-300">
                  {content}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AIInfoTooltip;
